import React, { useState, useEffect } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  Paper,
  Pagination,
  Typography,
  Dialog,
  Button,
  Divider,
  TextField,
  MenuItem,
} from "@mui/material";
import PaginationItem from "@mui/material/PaginationItem";
import moment from "moment";
import { styled } from "@mui/material/styles";
import { updateEditorPayment, getEditorAndProjectList } from "../../apis/apiList/editorPaymentApi";
import ClipLoader from "react-spinners/ClipLoader";
import theme from "../../theme/theme";
import { useMessage } from "../../context/MessageContext";
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#495967",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const CustomPaginationItem = styled(PaginationItem)(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.primary.main : "#495967",
  color: "#fff",
  borderRadius: "0px",
  fontWeight: selected ? "bold" : "normal",
  margin: "0 4px",
  maxHeight: "26px",
  padding: "0px",
  "&:hover": {
    backgroundColor: selected ? "#d40000" : "#1e2a33",
  },
  "&.Mui-selected": {
    backgroundColor: selected ? theme.palette.primary.main : "#495967",
    color: "#fff",
    padding: "0px",
  },
}));

const statusOptions = ['Completed', 'Active', 'Pending Approval', 'Not Started'];

function ProjectListWithPayment({ filters, editorId, userDetails, handleTotalAmount, handleFilterTotalAmount }) {
  const [projects, setProjects] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedProjectForPayment, setSelectedProjectForPayment] = useState(null);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { showMessage } = useMessage();
  const navigate = useNavigate();

  const rowsPerPage = 6;

  useEffect(() => {
    fetchProjectData();
  }, [filters, editorId]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getEditorAndProjectList(editorId, {
        params: filters,
        header: {
          'Content-Type': 'application/json',
          'x-access-token': userDetails.jwtToken,
        },
      });
      setProjects(response.projects || []);
      handleFilterTotalAmount(response);
      setPage(1);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      showMessage("Failed to fetch projects", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects;
  const paginatedProjects = filteredProjects.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const stageLabels = {
    scriptsAndVisuals: 'Scripts & Visuals',
    voiceOver: 'Voice Over',
    video: 'Video',
  };

  function getLatestApprovalStage(approvals) {
    if (!approvals) return "No approval data";
    const stages = Object.keys(stageLabels);
    const allApproved = stages.every((stage) => approvals[stage]);
    if (allApproved) return "Approved";
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!approvals[stage]) {
        return stageLabels[stage];
      }
    }
    return "No stage in progress";
  }

  const handleTogglePaymentStatus = async (project, newStatus) => {
    setIsUpdating(true);
    try {
      const response = await updateEditorPayment(project._id, { paymentStatus: newStatus }, {
        header: {
          'x-access-token': userDetails.jwtToken,
        }
      });
      const payment = response.editorPayment;
      setProjects((prevProjects) =>
        prevProjects.map((p) =>
          p._id === payment._id ? { ...p, isPaid: payment.isPaid } : p
        )
      );
      handleTotalAmount(payment);
      setIsUpdating(false);
      showMessage("Payment status updated successfully", "success");
      setConfirmOpen(false);
    } catch (err) {
      console.error("Failed to update payment status:", err);
      setIsUpdating(false);
      showMessage("Failed to update payment status", "error");
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="payment table">
          <TableHead>
            <TableRow>
              {/* <StyledTableCell>id</StyledTableCell> */}
              <StyledTableCell>Project Name</StyledTableCell>
              <StyledTableCell>Organization</StyledTableCell>
              <StyledTableCell align="left">Stage</StyledTableCell>
              <StyledTableCell align="left">Status</StyledTableCell>
              <StyledTableCell align="left">Amount</StyledTableCell>
              <StyledTableCell align="left" sx={{ paddingLeft: '48px' }}>Paid</StyledTableCell>
              <StyledTableCell align="left">Due Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <StyledTableCell colSpan={6} align="center">
                  <ClipLoader size={30} color={theme.palette.primary.main} />
                </StyledTableCell>
              </StyledTableRow>
            ) : paginatedProjects.length > 0 ? (
              paginatedProjects.map((project, i) => (
                <StyledTableRow key={i}>
                  {/* <StyledTableCell component="th" scope="row">
                    {project.projectData?._id}
                  </StyledTableCell> */}
                  <StyledTableCell component="th" scope="row" onClick={() => window.open(`/org/${project.organizationData?._id}/project/view/${project.projectData?._id}`, '_blank')} style={{ cursor: 'pointer' }}>
                    {project.projectData?.projectName || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row" onClick={() => window.open(`/org/${project.organizationData?._id}/projects`, '_blank')} style={{ cursor: 'pointer' }}>
                    {project.organizationData?.organizationName || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {getLatestApprovalStage(project.projectData?.approvals)}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ textTransform: "capitalize" }}>
                    {project.projectData?.status || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="left">${project.amount}</StyledTableCell>
                  
                  <StyledTableCell align="left">
                    <TextField
                      select
                      size="small"
                      value={project.isPaid ? "Paid" : "Pending"}
                      onChange={(e) => {
                        if(userDetails.role == 'admin' && userDetails.jobTitle == 'admin'){
                          setSelectedProjectForPayment(project);
                          setSelectedPaymentStatus(e.target.value === "Paid");
                          setConfirmOpen(true);
                        }
                      }}
                      variant="outlined"
                      sx={{
                        minWidth: 100,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "16px",
                          fontSize: "14px",
                          backgroundColor: project.isPaid ? "#d4edda" : "#fff3cd",
                        },
                        "& .MuiSelect-select": {
                          textAlign: "center",
                          fontWeight: 600,
                          color: project.isPaid ? "#155724" : "#856404",
                          padding: "4.5px 22px 4.5px 5px !important",
                        },
                      }}
                    >
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                    </TextField>
                  </StyledTableCell>

                  <StyledTableCell align="left">
                    {project.projectData?.dueDate
                      ? moment(project.projectData.dueDate).format("MM/DD/YYYY")
                      : "TBD"}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={6} align="center">
                  No project records found.
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        count={Math.ceil(filteredProjects.length / rowsPerPage)}
        page={page}
        onChange={handlePageChange}
        renderItem={(item) => <CustomPaginationItem {...item} />}
        siblingCount={1}
        boundaryCount={1}
        showFirstButton
        showLastButton
      />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <Box>
          <Typography variant="h3" gutterBottom p={2} mb={0}>
            Confirm Payment Status Update
          </Typography>
          <Divider />
          <Typography p={3}>
            Are you sure you want to mark this payment as{" "}
            <strong>{selectedPaymentStatus ? "Paid" : "Pending"}</strong>?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2} mb={2} mr={2}>
            <Button onClick={() => setConfirmOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleTogglePaymentStatus(selectedProjectForPayment, selectedPaymentStatus);
              }}
              disabled={isUpdating}
            >
              {isUpdating ? "Updating..." : "Update"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default ProjectListWithPayment;
